<script setup lang="ts">
import {Handle, HandleType, Position} from '@vue-flow/core';
import {CustomNodeProps} from "@/core/interfaces/flowchart/flowchart";
import CustomNodeTooltip from "@/flowcharts/Components/CustomNodeTooltip.vue";
import {computed} from "vue";
import CustomNodeActionChip from "@/flowcharts/Components/CustomNodeActionChip.vue";
import CustomNodeHint from "@/flowcharts/Components/CustomNodeHint.vue";

const props = defineProps<{
    id: string;
    editMode: boolean;
    position: { x: number; y: number };
    data: CustomNodeProps;
}>();

const emits = defineEmits(['action-click']);

const rawPosition = (handlePosition: string): Position => {
    return handlePosition.split('-')[0] as Position;
};

/**
 * Whether the node has an action that can be opened by clicking on it.
 */
const hasAction = computed((): boolean => {
    return ['survey', 'print', 'contact'].includes(props.data?.action_type ?? '');
});

const handleClick = () => {
    if (!hasAction.value) {
        return;
    }

    emits('action-click', props.data);
};

const style = (handlePosition: string): string => {
    switch (handlePosition.split('-')[1] || '') {
        case 'left':
            return 'left:20%';
        case 'right':
            return 'left:80%';
        default:
            return '';
    }
};

/**
 * Parse node handles from JSON string to object.
 */
const handles = computed((): { id: string; type: HandleType; position: string }[] | null => {
    return props.data.handles
        ? JSON.parse(props.data.handles) as { id: string, type: HandleType, position: string }[]
        : null;
});

</script>

<template>
  <div
    class="p-4 max-w-80 text-sm shadow cursor-pointer relative"
    :class="{
      'bg-primary': data.highlighted,
      'bg-gray': data.secondaryHighlighted,
      'bg-white rounded-lg': data.type !== 'output',
      'border-2 border-primary bg-white shadow-lg rounded-l-lg rounded-tr-lg rounded-br-none': data.type === 'output',
    }"
    @click="handleClick()"
  >
    <CustomNodeTooltip v-if="data.description" :data="data" class="absolute" />
    <div class="whitespace-pre-line">{{ data.label }}</div>
    <CustomNodeHint v-if="data.hint" :data="data" />
    <div
      v-if="data.action_type === 'survey' && data.surveyVerdict"
      class="mt-2 flex items-center gap-2 text-xs"
    >
      <span
        class="rounded-full px-2 py-0.5 font-bold text-white bg-secondary"
      >
        {{ data.surveyVerdict.score }}
      </span>
      <span class="font-medium">{{ data.surveyVerdict.title }}</span>
    </div>
    <Handle
      v-for="handle in handles"
      :id="'h' + id + '-' + handle.id"
      :key="'h' + id + '-' + handle.id"
      :type="handle.type"
      :position="rawPosition(handle.position)"
      :style="style(handle.position)"
    />
    <div v-if="editMode" class="absolute bottom-1 left-1 text-xs text-slate-300">
      {{ Math.round(position.x) }}, {{ Math.round(position.y) }}
    </div>
    <CustomNodeActionChip :data="data" class="absolute -bottom-3 right-2 bg-white rounded-full" />
  </div>
</template>
